import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { auth } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  
  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    
    pollPaymentStatus();
  }, [sessionId]);
  
  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds
    
    if (attempts >= maxAttempts) {
      setStatus('error');
      toast.error('Kunne ikke bekrefte betaling. Kontakt support hvis beløpet er trukket.');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`, {
        headers: {
          Authorization: `Bearer ${auth.getToken()}`
        }
      });
      
      const data = response.data;
      setPaymentData(data);
      
      if (data.payment_status === 'COMPLETED') {
        setStatus('success');
        toast.success('✅ Betaling vellykket!');
        
        // Reload user data to update subscription status
        setTimeout(() => {
          window.location.href = '/lexy';
        }, 2000);
      } else if (data.status === 'expired') {
        setStatus('error');
        toast.error('Betalingsøkten utløp. Prøv igjen.');
      } else {
        // Payment still processing, continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      
      if (attempts >= maxAttempts - 1) {
        setStatus('error');
        toast.error('Kunne ikke sjekke betalingsstatus. Prøv igjen.');
      } else {
        setTimeout(pollPaymentStatus, pollInterval);
      }
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        
        {/* Success State */}
        {status === 'success' && (
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center border-2 border-emerald-200">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-emerald-600" />
            </div>
            
            <h1 className="text-3xl font-bold text-slate-900 mb-3">
              Betaling vellykket!
            </h1>
            
            <p className="text-slate-600 mb-6">
              Velkommen til Lexy Pro! Du får nå tilgang til alle funksjoner.
            </p>
            
            {paymentData && (
              <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Beløp:</span>
                  <span className="font-semibold text-slate-900">
                    {paymentData.amount} {paymentData.currency.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Status:</span>
                  <span className="font-semibold text-emerald-600">Fullført</span>
                </div>
              </div>
            )}
            
            <Button
              onClick={() => navigate('/lexy')}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              size="lg"
            >
              Gå til Lexy Pro
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}
        
        {/* Checking State */}
        {status === 'checking' && (
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center border-2 border-slate-200">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
            </div>
            
            <h1 className="text-3xl font-bold text-slate-900 mb-3">
              Bekrefter betaling...
            </h1>
            
            <p className="text-slate-600">
              Vennligst vent mens vi bekrefter betalingen din.
            </p>
            
            <div className="mt-6 text-sm text-slate-500">
              Forsøk {attempts + 1} av 5
            </div>
          </div>
        )}
        
        {/* Error State */}
        {status === 'error' && (
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center border-2 border-red-200">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            
            <h1 className="text-3xl font-bold text-slate-900 mb-3">
              Noe gikk galt
            </h1>
            
            <p className="text-slate-600 mb-6">
              Vi kunne ikke bekrefte betalingen din. Hvis beløpet er trukket, kontakt oss.
            </p>
            
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/lexy')}
                className="w-full"
                variant="outline"
              >
                Tilbake til Lexy
              </Button>
              
              <Button
                onClick={() => window.location.reload()}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                Prøv igjen
              </Button>
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
};

export default PaymentSuccess;
